import type {
  DolomiteEmodeChain,
  DolomiteIsolationType,
} from '@1delta/data-sdk'

// Disabled-config sentinel (mirrors the Aave-v3 e-mode "asset not in this mode"
// entry): zero collateral, un-borrowable. Used for markets that are NOT members
// of a given category so a position can never mix categories within e-mode.
const DISABLED_BORROW_FACTOR = 340282366920938500000

// Base cross-margin liquidation penalty fraction. Dolomite's global
// `liquidationSpread` (e.g. 1.05 → 5% reward) is NOT exposed by the subgraph
// nor fetched by the on-chain global getters here (only `marginRatio` and the
// earnings rate are read), so we fall back to the documented protocol default
// of 5%. Category (e-mode) entries instead use the curated
// `liquidationRewardOverride` from `dolomite-emode.json`, which is real.
// TODO: wire the real per-market `liquidationSpread` (+ market premium) once a
// global/per-market getter or subgraph field is available.
const BASE_LIQUIDATION_PENALTY = 0.05

export type DolomiteMarketConfig = {
  category: string
  label: string
  collateralFactor: number
  borrowCollateralFactor: number
  borrowFactor: number
  debtDisabled: boolean
  collateralDisabled: boolean
  liquidationPenalty: number
  closeFactor: number
}

/**
 * Dolomite collateral factors from a margin ratio R and a margin premium p.
 * Consistent across base and e-mode — e-mode is simply premiums-ignored (p=0)
 * with R replaced by the category's `marginRatioOverride`:
 *   collateralFactor       = 1 / (1 + p)
 *   borrowCollateralFactor = 1 / ((1 + R) · (1 + p))   ← health = bDC/adjDebt < 1 at liq
 *   borrowFactor           = 1 + p
 */
export const dolomiteFactors = (p: number, R: number) => ({
  collateralFactor: 1 / (1 + p),
  borrowCollateralFactor: 1 / ((1 + R) * (1 + p)),
  borrowFactor: 1 + p,
})

/** Mode id of an isolation-mode vault: the position lives in the vault of `marketId`. */
export const DOLOMITE_ISOLATION_MODE_PREFIX = 'iso:'
export const dolomiteIsolationMode = (marketId: string | number | bigint) =>
  `${DOLOMITE_ISOLATION_MODE_PREFIX}${marketId}`

const DISABLED: Omit<DolomiteMarketConfig, 'category' | 'label'> = {
  collateralFactor: 0,
  borrowCollateralFactor: 0,
  borrowFactor: DISABLED_BORROW_FACTOR,
  debtDisabled: true,
  collateralDisabled: true,
  liquidationPenalty: 0,
  closeFactor: 1,
}

/**
 * Build the per-market `config` map: the base cross-margin mode `'0'` plus one
 * entry per e-mode category present on the chain, plus one entry per
 * ISOLATION-MODE VAULT (`iso:<marketId>`).
 *
 * Isolation is a COMPOSITION rule, not a risk override: a position inside the
 * vault of market M keeps the ordinary `R + premium` factors but may hold only
 * M's receipt plus `allowableCollateralMarketIds`, and borrow only
 * `allowableDebtMarketIds` (an EMPTY list is unrestricted). So mode `iso:M`
 * carries every market's base factors with `debtDisabled` /
 * `collateralDisabled` set from the allow-lists — and in the base mode `'0'`
 * (and every category) the receipt itself is DISABLED on both sides, because
 * only the vault can ever hold it. The pair book then derives `dsavETH × WETH`
 * under `iso:76` and nothing else, on both the origin (per `config_id`) and
 * the edge fallback, without a second config format. A market that belongs to a
 * category gets that category's (premiums-ignored, tighter-ratio) factors; a
 * market that does NOT belong gets a disabled entry — exactly the Aave-v3
 * `populateEModes` shape, so the generic helper's `configs[mode]` selection
 * works unchanged.
 *
 * @param marketId      this market's Dolomite marketId
 * @param marginPremium per-market premium p (0 for most)
 * @param R             global margin ratio (= liquidationRatio − 1)
 * @param emode         the chain's e-mode table (`dolomiteEmode()?.[chainId]`)
 */
export function buildDolomiteConfigs(
  marketId: string,
  marginPremium: number,
  R: number,
  isClosing: boolean,
  emode?: DolomiteEmodeChain,
  isolation?: DolomiteIsolationType[string],
): Record<string, DolomiteMarketConfig> {
  const borrowOnly = emode?.riskFeatures?.[marketId]?.feature === 'BORROW_ONLY'
  // This market is itself a vault factory's receipt: usable ONLY inside its
  // own vault mode below, never in a user-owned account.
  const isReceipt = !!isolation?.[marketId]

  const base = dolomiteFactors(marginPremium, R)
  const configs: Record<string, DolomiteMarketConfig> = {
    ['0']: {
      category: '0',
      label: 'Cross-margin',
      ...base,
      debtDisabled: isClosing || isReceipt,
      collateralDisabled: borrowOnly || isReceipt,
      liquidationPenalty: BASE_LIQUIDATION_PENALTY,
      closeFactor: 1,
    },
  }

  for (const [isoId, vault] of Object.entries(isolation ?? {})) {
    const mode = dolomiteIsolationMode(isoId)
    const label = `Isolation: ${vault.underlyingSymbol || isoId}`
    if (isoId === marketId) {
      configs[mode] = {
        category: mode,
        label,
        ...base,
        debtDisabled: true, // a receipt is never borrowable
        collateralDisabled: false,
        liquidationPenalty: BASE_LIQUIDATION_PENALTY,
        closeFactor: 1,
      }
      continue
    }
    const debtAllowed =
      vault.allowableDebtMarketIds.length === 0 ||
      vault.allowableDebtMarketIds.includes(marketId)
    const collateralAllowed =
      vault.allowableCollateralMarketIds.length === 0 ||
      vault.allowableCollateralMarketIds.includes(marketId)
    // Another receipt can never enter this vault, whatever the list says.
    if (isReceipt || (!debtAllowed && !collateralAllowed)) {
      configs[mode] = { category: mode, label, ...DISABLED }
      continue
    }
    configs[mode] = {
      category: mode,
      label,
      ...base,
      debtDisabled: isClosing || !debtAllowed,
      collateralDisabled: borrowOnly || !collateralAllowed,
      liquidationPenalty: BASE_LIQUIDATION_PENALTY,
      closeFactor: 1,
    }
  }

  if (!emode?.categories) return configs

  const marketCategory = emode.marketCategories?.[marketId]
  for (const [cat, param] of Object.entries(emode.categories)) {
    if (marketCategory === cat && !isReceipt) {
      // member: premiums ignored, category margin ratio. The curated
      // `liquidationRewardOverride` is already a fraction (e.g. 0.04 = 4%).
      const f = dolomiteFactors(0, param.marginRatioOverride)
      configs[cat] = {
        category: cat,
        label: cat,
        ...f,
        debtDisabled: isClosing,
        collateralDisabled: borrowOnly,
        liquidationPenalty: param.liquidationRewardOverride,
        closeFactor: 1,
      }
    } else {
      // not a member of this category → disabled within this mode
      configs[cat] = {
        category: cat,
        label: cat,
        collateralFactor: 0,
        borrowCollateralFactor: 0,
        borrowFactor: DISABLED_BORROW_FACTOR,
        debtDisabled: true,
        collateralDisabled: true,
        liquidationPenalty: 0,
        closeFactor: 1,
      }
    }
  }

  return configs
}

/**
 * Resolve a position's effective e-mode mode from the categories of the markets
 * it holds. Dolomite "segregated risk": the override applies only when EVERY
 * held market belongs to the SAME non-NONE category; any uncategorised or
 * mixed-category asset falls back to base mode `'0'`.
 */
export function resolveDolomiteMode(
  heldMarketIds: string[],
  emode?: DolomiteEmodeChain,
  isolationMarketId?: string,
): string {
  // A vault-owned account is in its vault's mode, full stop: the receipt it
  // holds is uncategorised, so an e-mode category can never apply on top.
  if (isolationMarketId !== undefined)
    return dolomiteIsolationMode(isolationMarketId)
  if (!emode?.marketCategories || heldMarketIds.length === 0) return '0'
  const cats = new Set<string>()
  for (const m of heldMarketIds) {
    const cat = emode.marketCategories[m]
    if (!cat) return '0' // an uncategorised asset disqualifies e-mode
    cats.add(cat)
  }
  return cats.size === 1 ? [...cats][0] : '0'
}
